import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as storageService from '../../services/storageService';
import { StaffUser, StaffRole, AuditEntry, STAFF_ROLES } from '../../types';
import {
    Users, Plus, Edit, Power, ClipboardList, Activity,
    Key, X, Eye, EyeOff, AlertCircle, ArrowLeft, HeartPulse
} from 'lucide-react';
import { AdminNavbarUser } from '../../components/AdminNavbarUser';
import StaffSessionsPanel from '../../components/StaffSessionsPanel';

// ========================== STAFF MODAL ==========================
interface StaffModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    hospitalId: string;
    editingStaff?: StaffUser | null;
    primaryColor: string;
}

const StaffModal: React.FC<StaffModalProps> = ({ isOpen, onClose, onSave, hospitalId, editingStaff, primaryColor }) => {
    const [name, setName] = useState('');
    const [role, setRole] = useState<StaffRole>('OPERATIVO');
    const [pin, setPin] = useState('');
    const [pinConfirm, setPinConfirm] = useState('');
    const [showPin, setShowPin] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (editingStaff) {
            setName(editingStaff.name);
            setRole(editingStaff.role);
            setPin(''); setPinConfirm('');
        } else {
            setName(''); setRole('OPERATIVO'); setPin(''); setPinConfirm('');
        }
        setError(null); setShowPin(false);
    }, [editingStaff, isOpen]);

    const handleSave = async () => {
        if (!name.trim()) { setError('El nombre es requerido.'); return; }
        if (!editingStaff && !pin) { setError('El PIN es requerido.'); return; }
        if (pin && !/^\d{4}$/.test(pin)) { setError('El PIN debe ser exactamente 4 dígitos numéricos.'); return; }
        if (pin && pin !== pinConfirm) { setError('Los PINs no coinciden.'); return; }

        setLoading(true); setError(null);
        if (editingStaff) {
            const updates: any = { name: name.trim(), role };
            if (pin) updates.pin = pin;
            const ok = await storageService.updateStaffUser(editingStaff.id, updates);
            if (ok) { onSave(); onClose(); } else setError('Error al actualizar el usuario.');
        } else {
            const result = await storageService.createStaffUser(hospitalId, { name: name.trim(), role, pin });
            if (result.success) { onSave(); onClose(); } else setError(result.error || 'Error al crear el usuario.');
        }
        setLoading(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-md shadow-2xl">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-slate-800">
                        {editingStaff ? 'Editar Usuario' : 'Nuevo Usuario de Staff'}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors p-1"><X className="w-5 h-5" /></button>
                </div>

                <div className="p-6 space-y-5">
                    {/* Name */}
                    <div>
                        <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 block">Nombre Completo</label>
                        <input
                            type="text" value={name} onChange={e => setName(e.target.value)}
                            placeholder="Ej: María García López"
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-800 text-sm focus:outline-none focus:ring-2 placeholder:text-slate-300"
                            style={{ '--tw-ring-color': primaryColor } as any}
                        />
                    </div>

                    {/* Role */}
                    <div>
                        <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 block">Rol</label>
                        <div className="space-y-2">
                            {STAFF_ROLES.map(r => (
                                <button
                                    key={r.value} type="button" onClick={() => setRole(r.value)}
                                    className={`w-full flex items-start gap-3 p-3 rounded-xl border transition-all text-left ${role === r.value
                                        ? 'border-current bg-opacity-5'
                                        : 'border-slate-100 bg-slate-50 hover:bg-slate-100'
                                        }`}
                                    style={role === r.value ? { borderColor: primaryColor, backgroundColor: `${primaryColor}08` } : {}}
                                >
                                    <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0`}
                                        style={{ borderColor: role === r.value ? primaryColor : '#CBD5E1' }}
                                    >
                                        {role === r.value && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: primaryColor }} />}
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-slate-800">{r.label}</p>
                                        <p className="text-xs text-slate-400 mt-0.5">{r.description}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* PIN */}
                    <div>
                        <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 block">
                            {editingStaff ? 'Nuevo PIN (dejar vacío para no cambiar)' : 'PIN Personal (4 dígitos)'}
                        </label>
                        <div className="relative">
                            <input
                                type={showPin ? 'text' : 'password'} value={pin}
                                onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                placeholder="••••"
                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-800 text-sm focus:outline-none focus:ring-2 pr-12 font-mono tracking-widest"
                                style={{ '--tw-ring-color': primaryColor } as any}
                            />
                            <button type="button" onClick={() => setShowPin(!showPin)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        {pin && (
                            <div className="mt-2">
                                <input
                                    type={showPin ? 'text' : 'password'} value={pinConfirm}
                                    onChange={e => setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                    placeholder="Confirmar PIN"
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-800 text-sm focus:outline-none focus:ring-2 font-mono tracking-widest"
                                    style={{ '--tw-ring-color': primaryColor } as any}
                                />
                            </div>
                        )}
                    </div>

                    {error && (
                        <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                            <p className="text-red-600 text-sm">{error}</p>
                        </div>
                    )}
                </div>

                <div className="p-6 pt-0 flex gap-3">
                    <button onClick={onClose} className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-bold text-sm transition-all">
                        Cancelar
                    </button>
                    <button
                        onClick={handleSave} disabled={loading}
                        className="flex-1 py-3 text-white rounded-xl font-bold text-sm transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                        style={{ backgroundColor: primaryColor, boxShadow: `0 4px 14px 0 ${primaryColor}30` }}
                    >
                        {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : null}
                        {editingStaff ? 'Guardar Cambios' : 'Crear Usuario'}
                    </button>
                </div>
            </div>
        </div>
    );
};


// ========================== MAIN COMPONENT ==========================
type AccesosTab = 'staff' | 'staff_sessions' | 'audit';

const AdminAccesos: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<AccesosTab>('staff');
    const [staff, setStaff] = useState<StaffUser[]>([]);
    const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingStaff, setEditingStaff] = useState<StaffUser | null>(null);
    const [togglingId, setTogglingId] = useState<string | null>(null);
    const [hospitalName, setHospitalName] = useState('');
    const [primaryColor, setPrimaryColor] = useState('#5B65DC');
    const [logoUrl, setLogoUrl] = useState<string | undefined>();

    const auth = storageService.getAuthData();
    const { hospitalId: urlHospitalId } = useParams<{ hospitalId: string }>();
    const resolvedHospitalId = urlHospitalId || auth?.hospitalId || '';
    const isStrictAdmin = !auth?.role || ['SUPER_ADMIN', 'GROUP_ADMIN', 'HOSPITAL_ADMIN'].includes(auth.role) || ['SUPER_ADMIN', 'GROUP_ADMIN', 'HOSPITAL_ADMIN'].includes(auth?.staffRole as string);

    useEffect(() => {
        if (!auth && !resolvedHospitalId) { navigate('/login'); return; }
        const isImpersonating = window.location.search.includes('impersonate=true');
        if (!isImpersonating && (resolvedHospitalId === 'ADMIN' || auth?.role === 'SUPER_ADMIN')) {
            navigate('/admin/dashboard');
            return;
        }
        if (!isStrictAdmin) {
            navigate('/staff');
            return;
        }

        const init = async () => {
            if (!resolvedHospitalId) return;
            const hospital = await storageService.getHospitalById(resolvedHospitalId);
            if (hospital) {
                setHospitalName(hospital.name);
                setPrimaryColor(hospital.branding?.primaryColor || '#5B65DC');
                setLogoUrl(hospital.branding?.logoUrl);
            }
            await loadData();
        };
        init();
    }, [resolvedHospitalId]);

    const loadData = async () => {
        if (!resolvedHospitalId) return;
        setLoading(true);
        const [staffData, auditData] = await Promise.all([
            storageService.getStaffByHospital(resolvedHospitalId),
            storageService.getAuditLog(resolvedHospitalId)
        ]);
        setStaff(staffData);
        setAuditLog(auditData);
        setLoading(false);
    };

    const handleToggleActive = async (member: StaffUser) => {
        setTogglingId(member.id);
        await storageService.updateStaffUser(member.id, { isActive: !member.isActive });
        await loadData();
        setTogglingId(null);
    };

    const activeCount = staff.filter(s => s.isActive).length;

    const ROLE_CONFIG = useMemo(() => ({
        HOSPITAL_ADMIN: { label: 'Admin Hospital', color: 'bg-purple-50 text-purple-600 border-purple-200' },
        FINANCIERO: { label: 'Financiero', color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
        OPERATIVO: { label: 'Operativo', color: 'bg-blue-50 text-blue-600 border-blue-200' },
    }), []);

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans">
            {/* ─── Header ─────────────────────────────────────── */}
            <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-100 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
                    <div className="flex items-center gap-3 sm:gap-4">
                        <button
                            onClick={() => {
                                if (auth?.role === 'GROUP_ADMIN') navigate('/c/' + (auth.groupId) + window.location.search);
                                else navigate((resolvedHospitalId ? `/staff/paneladmin/${resolvedHospitalId}` : '/staff/paneladmin') + window.location.search);
                            }}
                            className="p-2 sm:p-2.5 rounded-xl hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        {logoUrl ? (
                            <img src={logoUrl} alt="Logo" className="h-8 sm:h-10 w-auto object-contain" />
                        ) : (
                            <div className="p-2 rounded-xl" style={{ backgroundColor: `${primaryColor}15` }}>
                                <HeartPulse className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: primaryColor }} />
                            </div>
                        )}
                        <div className="min-w-0">
                            <h1 className="text-base sm:text-lg font-black text-slate-800 tracking-tight truncate">{hospitalName}</h1>
                            <p className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest">Accesos</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <AdminNavbarUser primaryColor={primaryColor} className="hidden md:flex" />
                    </div>
                </div>
            </header>

            {/* ─── Tab Bar ──────────────────────────────────── */}
            <div className="bg-white border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1">
                    <button
                        onClick={() => setActiveTab('staff')}
                        className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-widest border-b-2 transition-all ${activeTab === 'staff'
                            ? 'border-current text-slate-800'
                            : 'border-transparent text-slate-400 hover:text-slate-600'
                            }`}
                        style={activeTab === 'staff' ? { color: primaryColor, borderColor: primaryColor } : {}}
                    >
                        <Users className="w-4 h-4" />
                        Personal
                    </button>
                    <button
                        onClick={() => setActiveTab('staff_sessions')}
                        className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-widest border-b-2 transition-all ${activeTab === 'staff_sessions'
                            ? 'border-current text-slate-800'
                            : 'border-transparent text-slate-400 hover:text-slate-600'
                            }`}
                        style={activeTab === 'staff_sessions' ? { color: primaryColor, borderColor: primaryColor } : {}}
                    >
                        <Activity className="w-4 h-4" />
                        Sesiones Activas
                    </button>
                    <button
                        onClick={() => setActiveTab('audit')}
                        className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-widest border-b-2 transition-all ${activeTab === 'audit'
                            ? 'border-current text-slate-800'
                            : 'border-transparent text-slate-400 hover:text-slate-600'
                            }`}
                        style={activeTab === 'audit' ? { color: primaryColor, borderColor: primaryColor } : {}}
                    >
                        <ClipboardList className="w-4 h-4" />
                        Actividad
                    </button>
                </div>
            </div>

            {/* ─── Content ──────────────────────────────────── */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
                {loading ? (
                    <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-6 animate-pulse mt-6">
                       <div className="h-8 w-48 bg-slate-100 rounded-lg mb-8" />
                       <div className="space-y-4">
                           {[1, 2, 3, 4, 5].map(i => (
                               <div key={i} className="h-16 bg-slate-50/80 rounded-xl" />
                           ))}
                       </div>
                    </div>
                ) : activeTab === 'staff' ? (
                    <div>
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}12` }}>
                                    <Users className="w-5 h-5" style={{ color: primaryColor }} />
                                </div>
                                <div>
                                    <h2 className="text-lg font-black text-slate-800 tracking-tight">Personal del Hospital</h2>
                                    <p className="text-xs text-slate-400 font-medium">{activeCount} activo{activeCount !== 1 ? 's' : ''} de {staff.length} usuario{staff.length !== 1 ? 's' : ''}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => { setEditingStaff(null); setIsModalOpen(true); }}
                                className="text-white font-bold py-2.5 px-5 rounded-xl flex items-center gap-2 shadow-lg transition-all active:scale-95 text-sm"
                                style={{ backgroundColor: primaryColor, boxShadow: `0 4px 14px 0 ${primaryColor}30` }}
                            >
                                <Plus className="w-4 h-4" /> Agregar Usuario
                            </button>
                        </div>

                        {staff.length === 0 ? (
                            <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-12 text-center">
                                <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                                <p className="text-slate-500 font-bold mb-1">Sin usuarios de staff</p>
                                <p className="text-slate-400 text-sm">Crea el primer usuario para habilitar el acceso con doble PIN.</p>
                            </div>
                        ) : (
                            <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] overflow-hidden">
                                <table className="w-full">
                                    <thead className="bg-slate-50/80 border-b border-slate-100 text-left">
                                        <tr>
                                            <th className="py-4 px-6 text-xs font-bold text-slate-400 uppercase tracking-wider">Nombre</th>
                                            <th className="py-4 px-6 text-xs font-bold text-slate-400 uppercase tracking-wider">Rol</th>
                                            <th className="py-4 px-6 text-xs font-bold text-slate-400 uppercase tracking-wider">Estado</th>
                                            <th className="py-4 px-6 text-xs font-bold text-slate-400 uppercase tracking-wider">Alta</th>
                                            <th className="py-4 px-6 text-xs font-bold text-slate-400 uppercase tracking-wider"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {staff.map(member => (
                                            <tr key={member.id} className={`transition-colors hover:bg-slate-50 ${!member.isActive ? 'opacity-50' : ''}`}>
                                                <td className="py-4 px-6">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: primaryColor }}>
                                                            {member.name.charAt(0).toUpperCase()}
                                                        </div>
                                                        <span className="text-sm font-bold text-slate-800">{member.name}</span>
                                                    </div>
                                                </td>
                                                <td className="py-4 px-6">
                                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${ROLE_CONFIG[member.role]?.color}`}>
                                                        {ROLE_CONFIG[member.role]?.label || member.role}
                                                    </span>
                                                </td>
                                                <td className="py-4 px-6">
                                                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${member.isActive ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                        <span className={`w-1.5 h-1.5 rounded-full ${member.isActive ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                                        {member.isActive ? 'Activo' : 'Inactivo'}
                                                    </span>
                                                </td>
                                                <td className="py-4 px-6 text-xs text-slate-400">
                                                    {new Date(member.createdAt).toLocaleDateString()}
                                                </td>
                                                <td className="py-4 px-6">
                                                    <div className="flex items-center gap-2 justify-end">
                                                        <button
                                                            onClick={() => { setEditingStaff(member); setIsModalOpen(true); }}
                                                            className="p-1.5 text-slate-300 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                                                            title="Editar"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleToggleActive(member)}
                                                            disabled={togglingId === member.id}
                                                            className={`p-1.5 rounded-lg transition-all ${member.isActive
                                                                ? 'text-emerald-500 hover:bg-emerald-50'
                                                                : 'text-slate-300 hover:bg-slate-100 hover:text-slate-500'
                                                                } disabled:opacity-50`}
                                                            title={member.isActive ? 'Desactivar' : 'Activar'}
                                                        >
                                                            <Power className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                ) : activeTab === 'staff_sessions' ? (
                    <div>
                        <StaffSessionsPanel
                            hospitalId={resolvedHospitalId}
                            primaryColor={primaryColor}
                            theme="light"
                        />
                    </div>
                ) : (
                    <div>
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${primaryColor}12` }}>
                                <Activity className="w-5 h-5" style={{ color: primaryColor }} />
                            </div>
                            <div>
                                <h2 className="text-lg font-black text-slate-800 tracking-tight">Registro de Actividad</h2>
                                <p className="text-xs text-slate-400 font-medium">Últimas 100 acciones del personal</p>
                            </div>
                        </div>

                        {auditLog.length === 0 ? (
                            <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.03)] p-12 text-center">
                                <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                                <p className="text-slate-500 font-bold">Sin actividad registrada</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {auditLog.map(entry => (
                                    <div key={entry.id} className="bg-white border border-slate-100 rounded-2xl px-5 py-4 flex items-start gap-4 hover:shadow-[0_4px_24px_rgba(0,0,0,0.04)] transition-all">
                                        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold text-white" style={{ backgroundColor: primaryColor }}>
                                            {entry.staffName.charAt(0)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-sm font-bold text-slate-800">{entry.staffName}</span>
                                                <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-mono">{entry.action}</span>
                                                {entry.targetId && <span className="text-xs text-slate-400 font-mono">{entry.targetId}</span>}
                                            </div>
                                            {entry.detail && (
                                                <p className="text-xs text-slate-400 mt-1 truncate">{entry.detail}</p>
                                            )}
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                            <p className="text-xs font-mono text-slate-400">{new Date(entry.timestamp).toLocaleDateString()}</p>
                                            <p className="text-xs font-mono text-slate-300">{new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </main>

            <StaffModal
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingStaff(null); }}
                onSave={loadData}
                hospitalId={resolvedHospitalId}
                editingStaff={editingStaff}
                primaryColor={primaryColor}
            />
        </div>
    );
};

export default AdminAccesos;
